import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resources } from './data';
import type { ResourceData } from './ResourcePage';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';

const SUCCESS_STORIES_SLUGS = ['case-studies'];
const LEARNING_SLUGS = ['blogs'];

const HERO_STATS = [
  { value: '10', labelKey: 'caseStudies' },
  { value: '70+', labelKey: 'blogArticles' },
];

export default function ResourcesIndex() {
  const { t } = useTranslation();
  const successList = SUCCESS_STORIES_SLUGS.map((slug) => resources[slug]).filter(Boolean) as ResourceData[];
  const learningList = LEARNING_SLUGS.map((slug) => resources[slug]).filter(Boolean) as ResourceData[];

  return (
    <div className="pt-24">
      <SEO
        title="Resources"
        description="Case studies, blog articles and guides on last-mile delivery, fleet management, and AI-powered logistics. Learn from real success stories."
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Resources', url: 'https://geofleet.ai/resources' },
        ])}
      />
      {/* Hero with dual blurs and stats */}
      <section className="relative py-16 lg:py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        <div className="absolute top-0 right-0 w-[500px] h-[300px] bg-blue-500/[0.06] rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[250px] bg-emerald-500/[0.05] rounded-full blur-3xl -translate-x-1/2" />
        <div className="absolute top-1/2 left-1/3 w-[250px] h-[180px] bg-violet-500/[0.04] rounded-full blur-3xl" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            <i className="ri-book-open-line" />
            <span>{t('resources.badge')}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-gray-900 tracking-tight mb-4">
            {t('resources.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            {t('resources.subtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-12">
            {HERO_STATS.map((stat) => (
              <div key={stat.labelKey} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-gray-900">{stat.value}</div>
                <div className="text-sm text-gray-500 mt-0.5">{t(`resources.stats.${stat.labelKey}`)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories — blue accent */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-14">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <i className="ri-team-line text-blue-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('resources.successStories')}</h2>
            </div>
            <p className="text-sm text-gray-500 mb-6 max-w-2xl">{t('resources.successStoriesSub')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {successList.map((resource) => (
                <ResourceCard key={resource.slug} resource={resource} accent="blue" exploreLabel={t('resources.explore')} />
              ))}
            </div>
          </div>

          {/* Learning — emerald + violet mix */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <i className="ri-folder-open-line text-emerald-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">{t('resources.learning')}</h2>
            </div>
            <p className="text-sm text-gray-500 mb-6 max-w-2xl">{t('resources.learningSub')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {learningList.map((resource, i) => (
                <ResourceCard
                  key={resource.slug}
                  resource={resource}
                  accent={i === 0 ? 'emerald' : 'violet'}
                  exploreLabel={t('resources.explore')}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('resources.ctaTitle')}</h2>
          <p className="text-gray-600 mb-6">
            {t('resources.ctaSubtitle')}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25"
            >
              {t('resources.ctaTrial')}
              <i className="ri-arrow-right-line text-sm" />
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-emerald-700 border-2 border-emerald-500 hover:bg-emerald-50 transition-all"
            >
              <i className="ri-calendar-line text-emerald-600" />
              {t('resources.ctaDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const ACCENT_STYLES = {
  blue: { bg: '#eef5ff', color: '#2563eb' },
  emerald: { bg: '#D1FAE5', color: '#059669' },
  violet: { bg: '#EDE9FE', color: '#7C3AED' },
};

function ResourceCard({
  resource,
  accent,
  exploreLabel,
}: {
  resource: ResourceData;
  accent: 'blue' | 'emerald' | 'violet';
  exploreLabel: string;
}) {
  const style = ACCENT_STYLES[accent];
  return (
    <Link
      to={`/resources/${resource.slug}`}
      className="group block bg-white rounded-2xl border border-gray-100 p-6 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-300"
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300"
        style={{ backgroundColor: style.bg }}
      >
        <i className={`${resource.icon} text-xl`} style={{ color: style.color }} />
      </div>
      {resource.heroStats && resource.heroStats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {resource.heroStats.slice(0, 3).map((s) => (
            <span key={s.label} className="text-xs font-semibold text-gray-500">
              {s.value} <span className="font-normal text-gray-400">{s.label}</span>
            </span>
          ))}
        </div>
      )}
      <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
        {resource.name}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-4">
        {resource.description}
      </p>
      <span
        className="inline-flex items-center gap-1.5 text-sm font-semibold"
        style={{ color: style.color }}
      >
        {exploreLabel}
        <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}
